import React, { useState } from 'react';
import { Plus, Search, Edit2, Archive, Receipt, Users, RefreshCw, AlertCircle, RotateCcw } from 'lucide-react';
import { useAncillaryFees, useArchiveAncillaryFee, useRestoreAncillaryFee } from '../../hooks/useAncillaryFees';
import { formatCurrency } from '@/services/tuitionService';
import type { AncillaryFee } from '@/types/tuition.types';
import AncillaryFeeFormModal from '../../components/tuition/AncillaryFeeFormModal';
import AncillaryFeeAssignModal from '../../components/tuition/AncillaryFeeAssignModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';

export const AncillaryFeesPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canManageAncillary = hasPermission(Permission.FINANCE_ANCILLARY_MANAGE);

  const [searchTerm, setSearchTerm] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState<AncillaryFee | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'archive' | 'restore';
    feeId: string;
  } | null>(null);

  // Fetch fees from API
  // Request up to the backend cap (100) so the list isn't silently truncated
  // at the default 20 when an institution has many ancillary-fee types.
  const { data, isLoading, error, refetch } = useAncillaryFees({
    search: searchTerm || undefined,
    includeArchived,
    limit: 100,
  });

  const archiveMutation = useArchiveAncillaryFee();
  const restoreMutation = useRestoreAncillaryFee();

  const fees = data?.data || [];

  const handleCreate = () => {
    setSelectedFee(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (fee: AncillaryFee) => {
    setSelectedFee(fee);
    setIsFormModalOpen(true);
  };

  const handleAssign = (fee: AncillaryFee) => {
    setSelectedFee(fee);
    setIsAssignModalOpen(true);
  };

  const handleArchiveClick = (fee: AncillaryFee) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Archiver le frais annexe',
      message: `Êtes-vous sûr de vouloir archiver "${fee.name}" ? Cette action peut être annulée.`,
      variant: 'archive',
      feeId: fee.id,
    });
  };

  const handleRestoreClick = (fee: AncillaryFee) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Restaurer le frais annexe',
      message: `Voulez-vous restaurer "${fee.name}" ? Il sera de nouveau disponible pour les affectations.`,
      variant: 'restore',
      feeId: fee.id,
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog) return;

    try {
      if (confirmDialog.variant === 'archive') {
        await archiveMutation.mutateAsync(confirmDialog.feeId);
      } else {
        await restoreMutation.mutateAsync(confirmDialog.feeId);
      }
      setConfirmDialog(null);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setSelectedFee(null);
  };

  const handleAssignSuccess = () => {
    setIsAssignModalOpen(false);
    setSelectedFee(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Frais Annexes</h1>
          <p className="text-gray-500 mt-1">
            Configuration des frais annexes (attestations, duplicatas, etc.)
          </p>
        </div>
        {canManageAncillary && (
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            Nouveau Frais
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un frais annexe..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Inclure archivés
          </label>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">Erreur lors du chargement des frais annexes</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
          <p className="mt-2 text-gray-500">Chargement...</p>
        </div>
      )}

      {/* Fees Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Désignation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Récurrent
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Affectations
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {fees.map((fee) => (
                  <tr
                    key={fee.id}
                    className={`hover:bg-gray-50 ${!fee.isActive ? 'opacity-60 bg-gray-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-8 w-8 rounded-lg ${fee.isActive ? 'bg-primary-100' : 'bg-gray-200'} flex items-center justify-center mr-3`}>
                          <Receipt className={`h-4 w-4 ${fee.isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <span className={`font-medium ${fee.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                            {fee.name}
                          </span>
                          {!fee.isActive && (
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                              Archivé
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500 max-w-xs truncate">
                      {fee.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">
                      {formatCurrency(fee.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {fee.isRecurring ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                          Annuel
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                          Ponctuel
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-600">
                        {fee._count?.assignments || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {fee.isActive ? (
                          <>
                            {canManageAncillary && (
                              <button
                                onClick={() => handleEdit(fee)}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Modifier"
                              >
                                <Edit2 size={18} />
                              </button>
                            )}
                            {canManageAncillary && (
                              <button
                                onClick={() => handleAssign(fee)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Gérer les affectations"
                              >
                                <Users size={18} />
                              </button>
                            )}
                            {canManageAncillary && (
                              <button
                                onClick={() => handleArchiveClick(fee)}
                                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Archiver"
                                disabled={archiveMutation.isPending}
                              >
                                <Archive size={18} />
                              </button>
                            )}
                          </>
                        ) : (
                          canManageAncillary && (
                            <button
                              onClick={() => handleRestoreClick(fee)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Restaurer"
                              disabled={restoreMutation.isPending}
                            >
                              <RotateCcw size={18} />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {fees.length === 0 && (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun frais annexe</h3>
              <p className="mt-1 text-sm text-gray-500">
                {canManageAncillary
                  ? 'Commencez par créer un nouveau frais annexe.'
                  : 'Aucun frais annexe configuré pour le moment.'}
              </p>
              {canManageAncillary && (
                <button
                  onClick={handleCreate}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus size={18} />
                  Créer un frais
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pagination info */}
      {data?.meta && data.meta.total > 0 && (
        <div className="text-sm text-gray-500 text-center">
          {data.meta.total} frais annexe{data.meta.total > 1 ? 's' : ''} au total
        </div>
      )}

      {/* Form Modal */}
      {isFormModalOpen && (
        <AncillaryFeeFormModal
          fee={selectedFee}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedFee(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Assignment Modal */}
      {isAssignModalOpen && selectedFee && (
        <AncillaryFeeAssignModal
          fee={selectedFee}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedFee(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Archive/Restore Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          variant={confirmDialog.variant}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmDialog(null)}
          isLoading={archiveMutation.isPending || restoreMutation.isPending}
        />
      )}
    </div>
  );
};
